import React from "react";

import { TradeLogGradientBadge } from "@/components/trade-log-gradient-badge";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  getParseStageCapabilityMatrix,
  type ParseStageCapabilityCell,
} from "../../utils/pineIrUi";
import {
  STRATEGY_PINE_SECTION_DESC_CLASS,
  STRATEGY_PINE_SECTION_LABEL_CLASS,
  STRATEGY_PINE_DETAIL_TABLE_CLASS,
  STRATEGY_PINE_DETAIL_TABLE_SHELL_CLASS,
} from "./StrategyPineSectionCard";

function CapabilityCell({
  value,
  note,
}: {
  value: ParseStageCapabilityCell;
  note?: string;
}): React.ReactElement {
  if (value === "conditional") {
    return (
      <TradeLogGradientBadge tone="warning" className="whitespace-normal">
        {note ?? "Conditional"}
      </TradeLogGradientBadge>
    );
  }

  return (
    <TradeLogGradientBadge tone={value === "yes" ? "positive" : "negative"}>
      {value === "yes" ? "Yes" : "No"}
    </TradeLogGradientBadge>
  );
}

export function PineParseStageCapabilityMatrix(): React.ReactElement {
  const rows = getParseStageCapabilityMatrix();

  return (
    <Field data-strategy-pine-parse-capability="" className="min-w-0 gap-2">
      <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>
        What you can do at each parse stage
      </FieldLabel>
      <FieldDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
        After you click Parse. Partial merge also requires inject readiness (ready or needs review).
      </FieldDescription>

      <div
        data-strategy-pine-parse-capability-table=""
        data-strategy-pine-detail-table=""
        data-shadcn-data-table=""
        className={STRATEGY_PINE_DETAIL_TABLE_SHELL_CLASS}
      >
        <Table className={STRATEGY_PINE_DETAIL_TABLE_CLASS}>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="min-w-[200px]">Action</TableHead>
              <TableHead className="min-w-[120px]" data-column-align="center">
                Parse successful
              </TableHead>
              <TableHead className="min-w-[120px]" data-column-align="center">
                Partial parse
              </TableHead>
              <TableHead className="min-w-[120px]" data-column-align="center">
                Failed parse
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell className="whitespace-normal">
                  {row.hint ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className={cn(
                            "cursor-help border-b border-dotted border-gray-400",
                            STRATEGY_PINE_SECTION_DESC_CLASS
                          )}
                        >
                          {row.label}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="left" className="max-w-xs text-xs">
                        {row.hint}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    row.label
                  )}
                </TableCell>
                <TableCell data-column-align="center">
                  <div className="flex min-h-9 items-center justify-center">
                    <CapabilityCell value={row.full} />
                  </div>
                </TableCell>
                <TableCell data-column-align="center">
                  <div className="flex min-h-9 items-center justify-center">
                    <CapabilityCell value={row.partial} note={row.partialNote} />
                  </div>
                </TableCell>
                <TableCell data-column-align="center">
                  <div className="flex min-h-9 items-center justify-center">
                    <CapabilityCell value={row.failed} />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Field>
  );
}
